import { useRouter } from 'next/router'
import { format } from 'date-fns'
import { id } from 'date-fns/locale'
import InfiniteScroll from 'react-infinite-scroll-component'
import Image from 'next/image'

import { Icon } from '@wartek-id/icon'

import { BUKTI_KARYA_CATEGORIES } from 'app/ProfileRevampPage/constants'
import { BuktiKaryaListSectionitem } from 'app/BuktiKaryaPage/components/BuktiKaryaListSection'
import Header from 'app/ProfileRevampPage/components/SectionKarya/BuktiKarya/Header'
import { EmptyState } from 'app/ProfileRevampPage/components/EmptyState'
import { LoadingState } from 'app/ProfileRevampPage/components/LoadingState'
import { useBuktiKarya } from 'app/ProfileRevampPage/hooks/useBuktiKarya'
import { useSETrackEvent } from 'app/ProfileRevampPage/hooks/useSETrackEvent'
import { capitalizeEachWords } from 'utils/text'

const DocumentThumbnailDoc = ({ thumbnailUrl = null, title = null }) => {
  if (thumbnailUrl)
    return (
      <Image
        src={thumbnailUrl}
        alt={title}
        // +30% of width of container
        width={72}
        height={72}
        className="border-[#CACCCF] rounded-sm"
      />
    )

  return (
    <div className="flex items-center justify-center h-[72px] w-[72px] rounded bg-gray-20">
      <Icon color="inverse">text_snippet</Icon>
    </div>
  )
}

const BuktiKaryaCategoryIndex = () => {
  const { query } = useRouter()
  const { id: profileId } = query
  const category = query?.category as string
  const { SETrackClickBuktiKaryaItem } = useSETrackEvent()

  const {
    data: karyaList,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useBuktiKarya(profileId as string, category as string)
  const hasKaryaList = (karyaList ?? []).length > 0

  const onClickItem = (index, id) => {
    SETrackClickBuktiKaryaItem({
      buktiKaryaCategory: category,
      buktiKaryaLocation: index + 1,
      buktiKaryaId: id,
    })
  }

  return (
    <main className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header />
      <div className="py-16 px-7">
        {isLoading && (
          <LoadingState data-testid="profile-bukti-karya-loading" />
        )}

        {!isLoading && hasKaryaList && (
          <InfiniteScroll
            dataLength={karyaList.length}
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={<LoadingState />}
          >
            {karyaList.map((karya, index) => (
              <div
                key={`profile-bukti-karya-item-${karya.contentId}`}
                className="cursor-pointer"
                onClick={() => onClickItem(index, karya.contentId)}
              >
                <BuktiKaryaListSectionitem
                  href={`/bukti-karya/${karya.contentType.toLowerCase()}/${
                    karya.contentId
                  }`}
                  title={karya.title}
                  description={format(
                    new Date(karya.publishedAt),
                    'dd MMM yyyy',
                    {
                      locale: id,
                    }
                  )}
                  titleTextProps={{
                    className: 'text-ellipsis mb-1 break-all line-clamp-2',
                  }}
                  image={
                    <DocumentThumbnailDoc
                      thumbnailUrl={karya.thumbnailUrl ?? null}
                      title={karya.title}
                    />
                  }
                  titleInfoTextProps={{ color: 'critical' }}
                />
              </div>
            ))}
          </InfiniteScroll>
        )}

        {!isLoading && !hasKaryaList && (
          <div className="mt-20 px-3">
            <EmptyState
              data-testid="profile-bukti-karya-empty-state"
              title="Belum ada Bukti Karya"
              description={`Mari unggah Bukti Karya kategori ${
                BUKTI_KARYA_CATEGORIES?.[category]?.title ??
                capitalizeEachWords(category.replace(/_/g, ' '))
              }! Karya Anda bisa menginspirasi rekan-rekan lainnya.`}
            />
          </div>
        )}
      </div>
    </main>
  )
}

export default BuktiKaryaCategoryIndex
